<template>
  <div class="mx-10 ma-5">
    <v-card flat class="pa-5">
      <v-layout row v-if="errors">
        <v-flex>
          <Alert @dismissed="onDismissed" :text="errors"></Alert>
        </v-flex>
      </v-layout>
      <div>
        <PageTitleWithSearch
          :title="'Other Income List'"
          :btnName="'Create New Income Type'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/other-income/create'"
          :btnPermission="'other_income_types.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />
        <DataTableWithPagination
          :headers="headers"
          :items="incomeList"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="onPageChange"
          dense
        >
          <template #row="{ item, index }">
            <tr
              :key="item.uuid"
              :class="[
                { 'first-row': index === 0 },
                index % 2 === 0 ? 'dark-row' : 'light-row',
                'custom-row',
              ]"
            >
              <td class="text-left">{{ item.name }}</td>
              <td class="text-left">{{ item.gl_account_title }}</td>
              <td class="text-left">{{ item.tax_applicable }}</td>
              <td class="text-left">{{ item.description }}</td>
              <td class="text-left">
                <v-btn
                  v-if="hasPermission('other_income_types.edit')"
                  class="ma-1"
                  color="#FFE0B2"
                  :to="'/other-income/edit/' + item.uuid"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="ma-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                  ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="hasPermission('other_income_types.delete')"
                  class="my-1"
                  x-small
                  color="#FFCDD2"
                  @click.stop="dropMemberConfirm(item.uuid)"
                  fab
                  style="box-shadow: none;"
                >
                  <v-icon dark small color="#F44336">mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="my-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </div>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this Other Income Type?'"
      :title="'Drop Income'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import { OtherIncomeService } from "@/services/otherIncome-service.js";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Snackbars from "@/components/shared/Snackbars";
import { checkError } from "@/config/errors.js";
import Alert from "@/components/shared/Alert.vue";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "OtherIncomeList",
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    Snackbars,
    DataTableWithPagination,
    Alert,
  },
  data() {
    return {
      loading: false,
      confirmOpen: false,
      dropId: null,
      snackbar: false,
      successMsg: "",
      errors: null,
      search: "",
      incomeList: [],
      headers: [
        { text: "Income Name", align: "left", value: "name" },
        {
          text: "GL Account Title",
          align: "left",
          value: "gl_account_title",
        },
        { text: "Tax Applicable", align: "left", value: "tax_applicable" },
        { text: "Description", align: "left", value: "description" },
        { text: "Actions", align: "left", value: "actions" },
      ],
      page: 1,
      total: 0,
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  created() {
    this.fetchIncomeList();
  },
  watch: {
    search() {
      this.page = 1;
      this.fetchIncomeList();
    },
    page() {
      this.fetchIncomeList();
    },
  },
  methods: {
    async fetchIncomeList() {
      this.loading = true;
      try {
        const response = await OtherIncomeService.getIncomeAccountList(
          this.search,
          this.page
        );
        this.incomeList = response.data.data || [];
        this.total = response.data.meta.last_page || 0;
      } catch (error) {
        this.incomeList = [];
        this.total = 0;
        console.error("Error fetching Income List:", error);
      } finally {
        this.loading = false;
      }
    },
    onPageChange(newPage) {
      this.page = newPage;
    },
    searchChange(event) {
      this.search = event;
    },
    goToIncomeDetails(item) {
      this.$router.push(`/other-income/entries/${item.id}`);
    },
    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },
    cancelConfirm() {
      this.confirmOpen = false;
    },
    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },
    dropMember() {
      if (this.dropId) {
        this.loading = true;
        OtherIncomeService.dropMember(this.dropId)
          .then((response) => {
            this.incomeList = this.incomeList.filter(
              (order) => order.uuid !== this.dropId
            );
            this.snackbar = true;
            this.successMsg = response.message;
          })
          .catch((error) => {
            let data = error.response?.data?.error || "Unknown error";
            let errorOutput = checkError.errorFormatting(data);

            let errorsArray = [];
            if (Array.isArray(errorOutput)) {
              errorsArray = errorOutput.filter((e) => !!e && e.trim());
              if (errorsArray.length === 0) errorsArray = [data];
            } else if (errorOutput && errorOutput.trim()) {
              errorsArray = [errorOutput];
            } else {
              errorsArray = [data];
            }
            this.errors = errorsArray;
          })
          .finally(() => {
            this.loading = false;
            this.dropId = null;
          });
      }
    },
    closeSnackbar() {
      this.snackbar = false;
    },
    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
